import React, { useEffect, useState } from "react";
import { announcement } from "../../../config/announcement";
import "./AnnouncementPopup.scss";

// Generisches Ankündigungs-Popup. Inhalt & Aktivierung werden ausschließlich
// über src/config/announcement.ts gepflegt – diese Komponente bleibt unverändert.
const AnnouncementPopup: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const storageKey = `hp-announcement-dismissed-${announcement.id}`;

  useEffect(() => {
    if (!announcement.enabled) return;
    const wasDismissed = sessionStorage.getItem(storageKey);
    if (wasDismissed) {
      setDismissed(true);
      return;
    }
    const t = setTimeout(() => setOpen(true), announcement.showDelayMs ?? 2000);
    return () => clearTimeout(t);
  }, [storageKey]);

  useEffect(() => {
    if (!announcement.enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  function close() {
    setOpen(false);
    setDismissed(true);
    sessionStorage.setItem(storageKey, "1");
  }

  // Komplett aus, wenn deaktiviert.
  if (!announcement.enabled) return null;

  const { image, imageAlt, leftTag, leftTitle, date, body, highlights, address, contact, banner } =
    announcement;

  return (
    <>
      {dismissed && !open && banner && (
        <button className="hp-banner" onClick={() => setOpen(true)}>
          {banner.date && <span className="hp-banner__date">{banner.date}</span>}
          <span className="hp-banner__text">{banner.text}</span>
          <span className="hp-banner__arrow">→</span>
        </button>
      )}
      <div
        className={`hp-overlay${open ? " hp-open" : ""}`}
        onClick={(e) => e.target === e.currentTarget && close()}
        aria-hidden={!open}
      >
        <div
          className={`hp-modal${image ? "" : " hp-modal--no-image"}`}
          role="dialog"
          aria-modal="true"
          aria-label={leftTitle ? leftTitle.join(" ") : "Ankündigung"}
        >
          <button className="hp-close" onClick={close} aria-label="Schließen">
            ✕
          </button>

          {/* LEFT */}
          {image && (
            <>
              <div className="hp-left">
                <img className="hp-illustration" src={image} alt={imageAlt || ""} />
                {(leftTag || leftTitle) && (
                  <div className="hp-left-footer">
                    {leftTag && <div className="hp-left-tag">{leftTag}</div>}
                    {leftTitle && (
                      <div className="hp-left-title">
                        {leftTitle.map((line, i) => (
                          <React.Fragment key={i}>
                            {line}
                            {i < leftTitle.length - 1 && <br />}
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="hp-divider" />
            </>
          )}

          {/* RIGHT */}
          <div className="hp-right">
            <div className="hp-rule" />

            {date && (
              <div className="hp-date-badge">
                <div className="hp-date-icon">
                  <div className="hp-date-icon-month">{date.month}</div>
                  <div className="hp-date-icon-day">{date.day}</div>
                </div>
                <div>
                  <div className="hp-date-text">{date.full}</div>
                  {date.sub && <div className="hp-date-sub">{date.sub}</div>}
                </div>
              </div>
            )}

            {body.map((paragraph, i) => (
              <div
                key={i}
                className="hp-body"
                dangerouslySetInnerHTML={{ __html: paragraph }}
              />
            ))}

            {highlights && highlights.length > 0 && (
              <div className="hp-highlights">
                {highlights.map((h, i) => (
                  <div key={i} className="hp-chip">
                    {h}
                  </div>
                ))}
              </div>
            )}

            {address && (
              <div className="hp-address">
                <div className="hp-address-label">{address.label || "Ort"}</div>
                <div className="hp-address-text">
                  {address.lines.map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < address.lines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            {contact && contact.length > 0 && (
              <div className="hp-contact">
                {contact.map((line, i) => (
                  <React.Fragment key={i}>
                    <span dangerouslySetInnerHTML={{ __html: line }} />
                    {i < contact.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AnnouncementPopup;
